import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Database, Loader2, Trash2, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { devtoolsApi } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';

const errMsg = (e: unknown) => (e instanceof ApiError ? e.message : 'Erro');

/** Ferramentas do Super Administrador. Úteis enquanto o sistema está em desenvolvimento. */
export function DevToolsPage() {
  const qc = useQueryClient();
  const status = useQuery({
    queryKey: ['devtools', 'sample-data'],
    queryFn: devtoolsApi.sampleDataStatus,
  });
  const [confirmRemove, setConfirmRemove] = useState(false);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['devtools'] });
    void qc.invalidateQueries({ queryKey: ['leads'] });
    void qc.invalidateQueries({ queryKey: ['products'] });
    void qc.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const load = useMutation({
    mutationFn: devtoolsApi.loadSampleData,
    onSuccess: (r) => {
      toast.success(
        `Carregado: ${r.created} leads criados, ${r.updated} atualizados` +
          (r.scriptsCreated ? `, ${r.scriptsCreated} scripts criados` : '') +
          (r.scriptsUpdated ? `, ${r.scriptsUpdated} scripts padrão atualizados` : '') +
          (r.productsCreated ? `, ${r.productsCreated} produtos` : ''),
      );
      refresh();
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const remove = useMutation({
    mutationFn: devtoolsApi.removeSampleData,
    onSuccess: (r) => {
      toast.success(`${r.leads} lead(s) de exemplo removido(s)`);
      refresh();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const leads = status.data?.leads ?? 0;

  return (
    <div>
      <PageHeader
        title="Ferramentas"
        description="Recursos de desenvolvimento e teste. Só o Super Administrador vê esta página."
      />
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Database className="size-4" /> Dados do sistema
            </CardTitle>
            <CardDescription>
              Carrega os {status.data?.defaultScripts ?? 2} scripts padrão (pt-BR e en-US) e os
              produtos do funil que faltarem, mais {status.data?.fixtureRows ?? '~50'} carrinhos
              abandonados fictícios (nomes, telefones e e-mails inventados, no mesmo formato da
              tabela de webhooks). Serve para testar leads, ligações pelo simulador, campanhas e
              relatórios.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {status.isLoading ? (
              <Skeleton className="h-10" />
            ) : (
              <p className="text-sm">
                No banco agora: <span className="font-semibold tabular-nums">{leads}</span> lead(s)
                de exemplo ·{' '}
                <span className="font-semibold tabular-nums">{status.data?.scripts ?? 0}</span>{' '}
                script(s).
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => load.mutate()} disabled={load.isPending}>
                {load.isPending ? <Loader2 className="animate-spin" /> : <Database />}
                {leads ? 'Recarregar dados do sistema' : 'Carregar dados do sistema'}
              </Button>
              <Button
                variant="outline"
                className="text-destructive"
                disabled={!leads || remove.isPending}
                onClick={() => setConfirmRemove(true)}
              >
                {remove.isPending ? <Loader2 className="animate-spin" /> : <Trash2 />}
                Remover dados de exemplo
              </Button>
            </div>
            <Alert>
              <Wrench className="size-4" />
              <AlertTitle>Como funciona</AlertTitle>
              <AlertDescription>
                Scripts e produtos só são criados se faltarem; os que você já editou não mudam.
                Scripts padrão que nunca foram editados recebem as melhorias do sistema. Os leads
                entram marcados (id externo <code>sample:…</code>): carregar de novo atualiza em vez
                de duplicar, e remover apaga só eles, junto com as chamadas, SMS e vínculos de
                campanha deles. Produtos criados aqui ficam sem link de checkout; configure o link
                real em Produtos antes de testar o SMS. Campanhas criadas com esses leads ficam
                vazias; exclua-as em Campanhas.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={confirmRemove} onOpenChange={setConfirmRemove}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover os dados de exemplo?</AlertDialogTitle>
            <AlertDialogDescription>
              {leads} lead(s) de exemplo e tudo o que depende deles (chamadas, transcrições, SMS,
              vínculos de campanha) serão apagados. Leads reais não são afetados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              onClick={() => {
                setConfirmRemove(false);
                remove.mutate();
              }}
            >
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
